import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Send, Plus, Trash2, AlertCircle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const EMPTY_PRODUCT = { sku: "", name: "", price: "", qty: 1, vat: 21 };
const EMPTY_FORM = {
  customer_name: "",
  phone: "",
  email: "",
  county: "",
  city: "",
  postal_code: "",
  address: "",
  shipping_price: "17.99",
  shipping_vat: 21,
  products: [{ ...EMPTY_PRODUCT }],
  notes: "",
};

export default function PublicManualOrderForm() {
  // Read token from URL hash (#token=...) or query (?token=...). Hash keeps
  // it out of server access logs.
  const [token, setToken] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);
  // Lightweight SKU autocomplete from the public products endpoint.
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    const hash = (window.location.hash || "").replace(/^#/, "");
    const qs = new URLSearchParams(window.location.search);
    const hp = new URLSearchParams(hash);
    setToken(hp.get("token") || qs.get("token") || "");
  }, []);

  useEffect(() => {
    // Load public catalog (no auth needed — used only for SKU/name autocomplete)
    fetch(`${BACKEND}/api/public/products/catalog`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setCatalog((d?.items || []).slice(0, 2000)))
      .catch(() => setCatalog([]));
  }, []);

  const updateForm = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateProduct = (idx, k, v) =>
    setForm((f) => ({
      ...f,
      products: f.products.map((p, i) => (i === idx ? { ...p, [k]: v } : p)),
    }));
  const addProduct = () =>
    setForm((f) => ({ ...f, products: [...f.products, { ...EMPTY_PRODUCT }] }));
  const removeProduct = (idx) =>
    setForm((f) => ({ ...f, products: f.products.filter((_, i) => i !== idx) }));

  const onPickSku = (idx, sku) => {
    const m = catalog.find((p) => p.part_number === sku);
    if (m) {
      updateProduct(idx, "sku", m.part_number);
      updateProduct(idx, "name", m.name);
      // Backend catalog now returns `sale_price` already gross (cu TVA),
      // mirroring what employees see on the Products page.
      const price = m.sale_price ? Number(m.sale_price) : "";
      updateProduct(idx, "price", price ? price.toFixed(2) : "");
    } else {
      updateProduct(idx, "sku", sku);
    }
  };

  const totals = (() => {
    let gross = 0;
    for (const p of form.products) {
      const price = Number(p.price) || 0;
      const qty = Number(p.qty) || 0;
      gross += price * qty;
    }
    return gross + (Number(form.shipping_price) || 0);
  })();

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError("Lipsește token-ul de acces. Folosește linkul exact pe care ți l-a dat administratorul.");
      return;
    }
    const required = ["customer_name", "phone", "address", "county", "city"];
    const missing = required.filter((k) => !(form[k] || "").trim());
    if (missing.length) {
      setError(`Completează câmpurile obligatorii: ${missing.join(", ")}`);
      return;
    }
    if (!form.products.length || !form.products.some((p) => p.sku && p.price && p.qty)) {
      setError("Adaugă cel puțin un produs cu SKU, preț și cantitate.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        token,
        customer_name: form.customer_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        county: form.county.trim(),
        city: form.city.trim(),
        postal_code: form.postal_code.trim() || undefined,
        address: form.address.trim(),
        shipping_price: Number(form.shipping_price) || 0,
        shipping_vat: Number(form.shipping_vat) || 21,
        products: form.products
          .filter((p) => p.sku && p.price && p.qty)
          .map((p) => ({
            sku: p.sku.trim(),
            name: (p.name || "").trim() || `Produs ${p.sku}`,
            price: Number(p.price),
            qty: Number(p.qty),
            vat: Number(p.vat) || 21,
          })),
        notes: form.notes.trim() || undefined,
      };
      const r = await fetch(`${BACKEND}/api/public/orders/manual`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`);
      setDone({ order_id: d?.order?.id, total: d?.order?.total });
      setForm(EMPTY_FORM);
    } catch (e) {
      setError(e?.message || "Eroare necunoscută la trimitere.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-zinc-900 border border-emerald-700/40 rounded-2xl p-6 text-center" data-testid="public-manual-order-done">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400 mb-3" />
          <h2 className="text-xl font-bold mb-2">Comandă înregistrată ✓</h2>
          <p className="text-sm text-zinc-400 mb-1">Număr comandă:</p>
          <p className="text-2xl font-mono text-emerald-300 mb-4">{done.order_id}</p>
          <p className="text-sm text-zinc-400 mb-4">Total: <span className="text-zinc-100 font-semibold">{Number(done.total || 0).toFixed(2)} RON</span></p>
          <button
            data-testid="public-manual-order-new-btn"
            onClick={() => setDone(null)}
            className="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-500 text-white font-medium"
          >
            Adaugă altă comandă
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 py-8 px-4" data-testid="public-manual-order-form">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold">Comandă manuală — Bragus</h1>
          <p className="text-sm text-zinc-500 mt-1">Înregistrează o comandă telefonică nouă</p>
        </div>

        {error && (
          <div className="mb-4 bg-rose-500/10 border border-rose-700/40 rounded-lg p-3 text-sm text-rose-300 flex items-start gap-2" data-testid="public-form-error">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          {/* Customer */}
          <section className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-400">Client</h2>
            <Input label="Nume complet *" testId="pub-customer-name" value={form.customer_name} onChange={(v) => updateForm("customer_name", v)} />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Telefon *" testId="pub-phone" value={form.phone} onChange={(v) => updateForm("phone", v)} />
              <Input label="Email" testId="pub-email" value={form.email} onChange={(v) => updateForm("email", v)} />
            </div>
          </section>

          {/* Address */}
          <section className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-400">Adresă livrare</h2>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Județ *" testId="pub-county" value={form.county} onChange={(v) => updateForm("county", v)} />
              <Input label="Oraș *" testId="pub-city" value={form.city} onChange={(v) => updateForm("city", v)} />
            </div>
            <Input label="Adresă (stradă, număr, bloc, scară, ap.) *" testId="pub-address" value={form.address} onChange={(v) => updateForm("address", v)} />
            <Input label="Cod poștal" testId="pub-postal" value={form.postal_code} onChange={(v) => updateForm("postal_code", v)} />
          </section>

          {/* Products */}
          <section className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-400">Produse</h2>
              <button type="button" data-testid="pub-add-product" onClick={addProduct} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-zinc-700 hover:border-orange-500/50 text-zinc-300">
                <Plus className="h-3.5 w-3.5" /> Adaugă produs
              </button>
            </div>
            {form.products.map((p, idx) => (
              <div key={idx} className="bg-zinc-950/60 border border-zinc-800 rounded-lg p-3 space-y-2" data-testid={`pub-product-${idx}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-zinc-500">Produs #{idx + 1}</span>
                  {form.products.length > 1 && (
                    <button type="button" onClick={() => removeProduct(idx)} className="p-1 text-rose-400 hover:bg-rose-500/10 rounded">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-1">
                    <label className="text-[11px] text-zinc-500">SKU</label>
                    <input
                      data-testid={`pub-product-sku-${idx}`}
                      list="public-sku-list"
                      value={p.sku}
                      onChange={(e) => onPickSku(idx, e.target.value)}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5 text-sm font-mono"
                      placeholder="BRAG-..."
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[11px] text-zinc-500">Nume</label>
                    <input
                      data-testid={`pub-product-name-${idx}`}
                      value={p.name}
                      onChange={(e) => updateProduct(idx, "name", e.target.value)}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5 text-sm"
                      placeholder="auto-completat din SKU"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-[11px] text-zinc-500">Preț unit. (cu TVA)</label>
                    <input
                      data-testid={`pub-product-price-${idx}`}
                      type="number" step="0.01" min="0"
                      value={p.price}
                      onChange={(e) => updateProduct(idx, "price", e.target.value)}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5 text-sm text-right"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-zinc-500">Cantitate</label>
                    <input
                      data-testid={`pub-product-qty-${idx}`}
                      type="number" step="1" min="1"
                      value={p.qty}
                      onChange={(e) => updateProduct(idx, "qty", e.target.value)}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5 text-sm text-right"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-zinc-500">TVA %</label>
                    <input
                      data-testid={`pub-product-vat-${idx}`}
                      type="number" step="1" min="0"
                      value={p.vat}
                      onChange={(e) => updateProduct(idx, "vat", e.target.value)}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5 text-sm text-right"
                    />
                  </div>
                </div>
              </div>
            ))}
            <datalist id="public-sku-list">
              {catalog.map((p) => (
                <option key={p.part_number} value={p.part_number}>{p.name}</option>
              ))}
            </datalist>
          </section>

          {/* Shipping + summary */}
          <section className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-400">Livrare</h2>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Preț livrare (cu TVA)" testId="pub-ship-price" value={form.shipping_price} onChange={(v) => updateForm("shipping_price", v)} type="number" />
              <Input label="TVA livrare %" testId="pub-ship-vat" value={form.shipping_vat} onChange={(v) => updateForm("shipping_vat", v)} type="number" />
            </div>
            <div className="pt-3 border-t border-zinc-800 flex justify-between text-sm">
              <span className="text-zinc-400">Total estimativ</span>
              <span className="text-xl font-bold text-orange-300" data-testid="pub-total">{Number(totals).toFixed(2)} RON</span>
            </div>
          </section>

          <button
            type="submit"
            disabled={submitting}
            data-testid="public-submit-btn"
            className="w-full bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 disabled:opacity-50 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {submitting ? "Se trimite..." : "Înregistrează comanda"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Input({ label, testId, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-[11px] text-zinc-500 block mb-1">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-orange-500/50"
      />
    </div>
  );
}
